/**
 * Saudi Arabia License Plate Utilities (Frontend)
 *
 * Mirrors backend/src/lib/plate.ts. Used for live in-form validation and
 * AR↔EN auto-conversion as the user types.
 *
 * Source of truth: Wikipedia + KSA General Directorate of Traffic.
 */

export const LATIN_TO_ARABIC: Record<string, string> = {
  A: 'ا', B: 'ب', J: 'ح', D: 'د', R: 'ر',
  S: 'س', X: 'ص', T: 'ط', E: 'ع', G: 'ق',
  K: 'ك', L: 'ل', Z: 'م', N: 'ن', H: 'ه',
  U: 'و', V: 'ى',
};

export const ARABIC_TO_LATIN: Record<string, string> = Object.fromEntries(
  Object.entries(LATIN_TO_ARABIC).map(([latin, ar]) => [ar, latin])
);

export const ALLOWED_LATIN_LETTERS = Object.keys(LATIN_TO_ARABIC);
export const ALLOWED_ARABIC_LETTERS = Object.keys(ARABIC_TO_LATIN);

export const BANNED_LATIN_COMBOS = new Set([
  'SEX', 'ASS', 'USA', 'AID', 'GAY', 'BAD', 'DUI', 'KKK', 'SOB',
  'TIT', 'XXX',
]);

export interface PlateValidationResult {
  valid: boolean;
  digits: { valid: boolean; error?: string };
  lettersEn: { valid: boolean; error?: string; invalidChars?: string[] };
  lettersAr: { valid: boolean; error?: string; invalidChars?: string[] };
}

export function validatePlate(input: {
  digits: string;
  lettersEn?: string | null;
  lettersAr?: string | null;
}): PlateValidationResult {
  const result: PlateValidationResult = {
    valid: true,
    digits: { valid: true },
    lettersEn: { valid: true },
    lettersAr: { valid: true },
  };

  // Digits
  if (!input.digits) {
    result.digits = { valid: false, error: 'Required' };
  } else if (!/^[0-9]{4}$/.test(input.digits)) {
    result.digits = { valid: false, error: 'Must be exactly 4 digits' };
  }

  // Latin letters
  if (input.lettersEn && input.lettersEn.length > 0) {
    const upper = input.lettersEn.toUpperCase();
    if (!/^[A-Z]{1,3}$/.test(upper)) {
      result.lettersEn = { valid: false, error: 'Letters only' };
    } else {
      const invalid = upper.split('').filter((c) => !ALLOWED_LATIN_LETTERS.includes(c));
      if (invalid.length > 0) {
        result.lettersEn = {
          valid: false,
          error: `Not allowed: ${invalid.join(', ')}`,
          invalidChars: invalid,
        };
      } else if (upper.length === 3 && BANNED_LATIN_COMBOS.has(upper)) {
        result.lettersEn = { valid: false, error: `"${upper}" is banned on plates` };
      } else if (upper.length < 3) {
        result.lettersEn = { valid: false, error: 'Need 3 letters' };
      }
    }
  } else {
    result.lettersEn = { valid: false, error: 'Required' };
  }

  // Arabic letters
  if (input.lettersAr && input.lettersAr.length > 0) {
    const trimmed = input.lettersAr.replace(/\s/g, '');
    const chars = Array.from(trimmed);
    const invalid = chars.filter((c) => !ALLOWED_ARABIC_LETTERS.includes(c));
    if (invalid.length > 0) {
      result.lettersAr = {
        valid: false,
        error: `غير مسموح: ${invalid.join(' ')}`,
        invalidChars: invalid,
      };
    } else if (chars.length > 0 && chars.length < 3) {
      result.lettersAr = { valid: false, error: 'يلزم 3 أحرف' };
    }
  }

  result.valid =
    result.digits.valid &&
    result.lettersEn.valid &&
    (input.lettersAr ? result.lettersAr.valid : true);

  return result;
}

export function latinToArabic(latin: string): string {
  if (!latin) return '';
  let result = '';
  for (const c of latin.toUpperCase()) {
    const ar = LATIN_TO_ARABIC[c];
    if (ar) result += ar;
  }
  return result;
}

export function arabicToLatin(arabic: string): string {
  if (!arabic) return '';
  let result = '';
  for (const c of arabic.replace(/\s/g, '')) {
    const latin = ARABIC_TO_LATIN[c];
    if (latin) result += latin;
  }
  return result;
}

const WESTERN_TO_EASTERN: Record<string, string> = {
  '0': '٠', '1': '١', '2': '٢', '3': '٣', '4': '٤',
  '5': '٥', '6': '٦', '7': '٧', '8': '٨', '9': '٩',
};

export function digitsToEasternArabic(digits: string): string {
  return digits.split('').map((d) => WESTERN_TO_EASTERN[d] ?? d).join('');
}
